// eslint-disable-file no-use-before-define
import ajax from '@/lib/ajax';
import cookieUtils from '@/lib/cookieUtils';
import { Module, Mutation, Action, VuexModule, getModule } from 'vuex-module-decorators';
import { AuthModule } from '@/store/Auth/AuthModule';
import i18n from "@/main";
import store from '@/store';
import PageRoutes from '@/router/PageRoutes';
import NavigationMenuItem from '@/models/Auth/NavigationMenuItem'
import NavigationMenu from '@/models/Auth/NavigationMenu';
import ErrorMessage from '@/models/Auth/ErrorMessage';
import Errors from '@/models/Auth/Errors';
import PermissionType from '@/models/Settings/Roles/PermissionType';
import PermissionUtils from '@/lib/permissionUtils';
import FeatureToggleCode from "@/models/Settings/Institutions/FeatureToggleCode";

export interface IAppState {
    navigationMenu: NavigationMenu;
    dialogLoading: boolean;
    lastError: ErrorMessage;
    lastErrorVisible: boolean;
    newLoanId: string;
}

@Module({ dynamic: true, store, name: 'AppModule', namespaced: true })
class AppStore extends VuexModule implements IAppState {

    public navigationMenu: NavigationMenu = new NavigationMenu();
    public dialogLoading: boolean = false;
    public lastError: ErrorMessage = null;
    public lastErrorVisible: boolean = false;
    public newLoanId: string = '';

    @Action
    async openExternalDocumentationURL(urlSubPath: string) {
        // Calls the go backend to get the nonce for authentication before opening in a new page
        await ajax.get("/portal-backend/document360/nonce")
        .then(response => {
            window.open(`https://knowledgebase.saascada.io/docs${urlSubPath}?nonce=` + response.data.nonce);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async clearErrorMessage() {
        this.context.commit("setErrorMessage", null);
    }

    @Action
    async handleApiError(error: any) {
        // First handle errors in responses, the request was made and the server responded
        // with a status code that falls out of the range of 2xx, in this case response.data,
        // response.status and response.headers should be available
        if (error.response) {
            // Handle specific error cases for OutOfData and NotFound errors with refresh button set as true
            if (error.response.data.code == Errors.OutOfDate || error.response.data.code == Errors.NotFound) {
                this.context.commit("setErrorMessage", new ErrorMessage(error.response.data.message, error.response.data.code, true));
            }
            // Handle specific errors related to payments, only if paymentErrors is defined
            else if (error.response.data.paymentErrors) {
                const firstPaymentError = error.response.data.paymentErrors[0].errors[0].error;
                const errorCode = error.response.data.status;
                this.context.commit("setErrorMessage", new ErrorMessage(firstPaymentError, errorCode, false));
            }
            // Else handle other errors more generically here with refresh button set as false
            else {
                // Handle different error response formats for the message/code
                const errorMessage = error.response.data.message ? error.response.data.message : error.response.data.title;
                const errorCode = error.response.data.code ? error.response.data.code : error.response.data.status;
                this.context.commit("setErrorMessage", new ErrorMessage(errorMessage, errorCode, false));
            }
        }
        // Else handle errors in requests, the request was made but no response was received, `error.request` is
        // an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
        else if (error.request) {
            this.context.commit("setErrorMessage", new ErrorMessage("Server unavailable.", "SERVER_UNAVAILABLE", false));
            console.error("SERVER_UNAVAILABLE", error);
        }
        // Otherwise something happened in setting up the request that triggered an error, handle this generically
        else {
            this.context.commit("setErrorMessage", new ErrorMessage("Request could not be sent.", "REQUEST_SETUP_FAILED", false));
            console.error("REQUEST_SETUP_FAILED", error);
        }
    }

    @Action
    async setSimpleErrorMessage(message: string) {
        // Used when we want to trigger a simple error message instead of one with a specific pattern
        this.context.commit("setErrorMessage", new ErrorMessage(message, "", false));
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    public async setErrorMessage(errorMessage: ErrorMessage) {
        this.lastError = errorMessage;
        // We need to set this to false first to ensure it's value changes
        // as the error could have been canceled by dissmissable or programmatically
        this.lastErrorVisible = false;
        if (errorMessage != null) {
            this.lastErrorVisible = true;
        }
    }

    @Mutation
    public async setNavigationMenu() {

        this.navigationMenu = new NavigationMenu();

        // Accounts Menu - Each account type has a separate show/hide setting in the backend
        const institutionConfig = AuthModule.me.institution.config;
        const institutionLabels = AuthModule.me.institution.config.labels
        if (institutionConfig.showThirdPartyAccounts) {
            this.navigationMenu.accountsMenuItems.push(new NavigationMenuItem(PageRoutes.ThirdPartyAccounts, institutionLabels.thirdPartyAccounts, "mdi-bank-plus", PermissionType.ThirdPartyAccountsRead));
        }
        if (institutionConfig.showInstitutionAccounts) {
            this.navigationMenu.accountsMenuItems.push(new NavigationMenuItem(PageRoutes.InstitutionAccounts, institutionLabels.institutionAccounts, "mdi-bank", PermissionType.InstitutionalAccountsRead));
        }
        if (institutionConfig.showDepositAccounts) {
            this.navigationMenu.accountsMenuItems.push(new NavigationMenuItem(PageRoutes.DepositAccounts, i18n.t("navigationMenuText.depositAccounts"), "mdi-bank-transfer-in", PermissionType.DepositAccountsRead));
        }
        if (institutionConfig.showLendingAccounts) {
            this.navigationMenu.accountsMenuItems.push(new NavigationMenuItem(PageRoutes.LendingAccounts, i18n.t("navigationMenuText.lendingAccounts"), "mdi-bank-transfer-out", PermissionType.LendingAccountsRead));
        }

        // Products Menu
        this.navigationMenu.productsMenuItems.push(new NavigationMenuItem(PageRoutes.Products, i18n.t("navigationMenuText.products"), "mdi-application-edit-outline", PermissionType.ProductsRead));
        this.navigationMenu.productsMenuItems.push(new NavigationMenuItem(PageRoutes.ProductIssues, i18n.t("navigationMenuText.productIssues"), "mdi-application-cog-outline", PermissionType.ProductsRead));
        this.navigationMenu.productsMenuItems.push(new NavigationMenuItem(PageRoutes.ProductMigrations, i18n.t("navigationMenuText.productMigrations"), "mdi-application-export", PermissionType.ProductsRead));
        this.navigationMenu.productsMenuItems.push(new NavigationMenuItem(PageRoutes.RateSources, i18n.t("navigationMenuText.rateSources"), "mdi-chart-timeline-variant", PermissionType.ProductsRead));
        this.navigationMenu.productsMenuItems.push(new NavigationMenuItem(PageRoutes.SubTransactionTypes, i18n.t("navigationMenuText.subTransactionTypes"), "mdi-format-list-bulleted-type", PermissionType.ProductsRead));

        // Accounting Menu - all pages are behind the feature toggle
        if (PermissionUtils.isFeatureToggleEnabled(FeatureToggleCode.GeneralLedger)) {
            this.navigationMenu.accountingMenuItems.push(new NavigationMenuItem(PageRoutes.GeneralLedgerAccounts, i18n.t("navigationMenuText.generalLedgerAccounts"), "mdi-bank-check", PermissionType.AccountingRead));
            this.navigationMenu.accountingMenuItems.push(new NavigationMenuItem(PageRoutes.JournalEntries, i18n.t("navigationMenuText.journalEntries"), "mdi-book-edit-outline", PermissionType.AccountingRead));
        }

        // Reporting Menu
        this.navigationMenu.reportingMenuItems.push(new NavigationMenuItem(PageRoutes.Reports, i18n.t("navigationMenuText.reports"), "mdi-finance", PermissionType.ReportsRead));

        // Settings Menu
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.Users, i18n.t("navigationMenuText.users"), "mdi-account-circle-outline", PermissionType.UsersRead));
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.Roles, i18n.t("navigationMenuText.roles"), "mdi-shield-lock-outline", PermissionType.RolesRead));
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.AppMediums, i18n.t("navigationMenuText.appMediums"), "mdi-key-chain-variant", PermissionType.AppMediumsRead));
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.Webhooks, i18n.t("navigationMenuText.webhooks"), "mdi-webhook", PermissionType.WebhooksRead));
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.Institutions, i18n.t("navigationMenuText.institutions"), "mdi-office-building-cog-outline", PermissionType.InstitutionSettingsRead));
        this.navigationMenu.settingsMenuItems.push(new NavigationMenuItem(PageRoutes.Regions, i18n.t("navigationMenuText.regions"), "mdi-map-marker-radius-outline", PermissionType.InstitutionSettingsRead));

        // Customers Menu (for UX users only)
        // Note: The permissions here are DepositAccountsRead because there is currently no specific permission for Customers alone
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            this.navigationMenu.customerMenuItems.push(new NavigationMenuItem(PageRoutes.Customers, i18n.t("navigationMenuText.customers"), "mdi-badge-account-outline", PermissionType.DepositAccountsRead));
            this.navigationMenu.customerMenuItems.push(new NavigationMenuItem(PageRoutes.People, i18n.t("navigationMenuText.people"), "mdi-account-multiple", PermissionType.DepositAccountsRead));
            this.navigationMenu.customerMenuItems.push(new NavigationMenuItem(PageRoutes.Organisations, i18n.t("navigationMenuText.organisations"), "mdi-domain", PermissionType.DepositAccountsRead));
        }

        // Documentation Menu (depending on permissions)
        if (AuthModule.me.institution.config.coreApiSwagger) {
            // Note: The condition here is temporary, presumably once this is fully switched over this will always be on and visible for everyone
            this.navigationMenu.documentationMenuItems.push(new NavigationMenuItem(PageRoutes.CoreApiDocumentation, i18n.t("navigationMenuText.coreApi"), "mdi-api", PermissionType.PortalRead));
        }
        if (AuthModule.me.institution.config.fiApiAccess) {
            this.navigationMenu.documentationMenuItems.push(new NavigationMenuItem(PageRoutes.FiDocumentation, i18n.t("navigationMenuText.fiApi"), "mdi-api", PermissionType.PortalRead));
        }
        if (AuthModule.me.institution.config.customFiSwagger) {
            this.navigationMenu.documentationMenuItems.push(new NavigationMenuItem(PageRoutes.FiCustomDocumentation, i18n.t("navigationMenuText.fiApiCustom"), "mdi-api", PermissionType.PortalRead));
        }
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            this.navigationMenu.documentationMenuItems.push(new NavigationMenuItem(PageRoutes.UxDocumentation, i18n.t("navigationMenuText.uxApi"), "mdi-api", PermissionType.PortalRead));
        }
        if (AuthModule.me.institution.config.customUxSwagger && PermissionUtils.allowUxApiAccess()) {
            this.navigationMenu.documentationMenuItems.push(new NavigationMenuItem(PageRoutes.UxCustomDocumentation, i18n.t("navigationMenuText.uxApiCustom"), "mdi-api", PermissionType.PortalRead));
        }
    }

    @Mutation
    resetState() {
        cookieUtils.deleteCookie('SaaScada.AuthToken', undefined);
    }
}

export const AppModule = getModule(AppStore);